import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { VideoEmbed } from '../../components/VideoEmbed';
import { Check, TrendingUp } from 'lucide-react';

export default function PatientExercises() {
  const [pres, setPres] = useState([]);
  const [exs, setExs] = useState([]);
  const [filter, setFilter] = useState('');
  const [compliance, setCompliance] = useState(null);
  const [logging, setLogging] = useState(null);

  useEffect(() => {
    api.get('/exercises/prescriptions').then(r => setPres(r.data));
    api.get('/exercises').then(r => setExs(r.data));
    api.get('/auth/me').then(me => {
      api.get(`/exercise-logs/${me.data.user_id}/compliance?days=30`)
        .then(r => setCompliance(r.data)).catch(()=>{});
    });
  }, []);

  const markDone = async (exerciseId) => {
    if (!pres[0]) return;
    setLogging(exerciseId);
    try {
      await api.post('/exercise-logs', {
        prescription_id: pres[0].prescription_id,
        exercise_id: exerciseId,
      });
      const me = await api.get('/auth/me');
      const c = await api.get(`/exercise-logs/${me.data.user_id}/compliance?days=30`);
      setCompliance(c.data);
    } catch (e) {
      alert('Could not log: ' + (e.response?.data?.detail || e.message));
    } finally { setLogging(null); }
  };

  const visible = exs.filter(e => !filter || e.category === filter);

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">YOUR ROUTINE</div>
          <h1 className="text-4xl font-heading font-bold tracking-tight">Exercises</h1>
        </div>
        {compliance && (
          <div className="surface border border-soft rounded-lg p-3 min-w-[180px]" data-testid="compliance-card">
            <div className="text-[10px] uppercase tracking-wider text-fg-muted flex items-center gap-1">
              <TrendingUp className="w-3 h-3"/> 30-DAY COMPLIANCE
            </div>
            <div className="text-2xl font-heading font-bold text-brand">{compliance.compliance_percent}%</div>
            <div className="text-xs text-fg-muted">{compliance.active_days} of {compliance.window_days} days active</div>
          </div>
        )}
      </div>

      {pres[0] && pres[0].exercises?.length > 0 && (
        <div className="bento-card mb-6">
          <h3 className="font-heading font-bold text-xl mb-3">Prescribed by your therapist</h3>
          {pres[0].notes && <div className="text-sm text-fg-secondary mb-4 surface border border-soft p-3 rounded">{pres[0].notes}</div>}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {pres[0].exercises.map(ex => (
              <Card key={ex.exercise_id} ex={ex} highlight
                    onDone={()=>markDone(ex.exercise_id)}
                    logging={logging === ex.exercise_id} />
            ))}
          </div>
        </div>
      )}

      <div className="bento-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-xl">Library</h3>
          <select className="input max-w-[200px]" value={filter} onChange={e=>setFilter(e.target.value)} data-testid="ex-filter">
            <option value="">All categories</option>
            <option value="mobility">Mobility</option>
            <option value="strength">Strength</option>
            <option value="balance">Balance</option>
            <option value="stretching">Stretching</option>
            <option value="stability">Stability</option>
            <option value="rehab">Rehab</option>
          </select>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {visible.map(ex => <Card key={ex.exercise_id} ex={ex} />)}
        </div>
      </div>
    </div>
  );
}

function Card({ ex, highlight, onDone, logging }) {
  return (
    <div className={`surface border ${highlight ? 'border-[#003D80]' : 'border-soft'} rounded-lg overflow-hidden`} data-testid={`patient-ex-${ex.exercise_id}`}>
      {ex.video_url ? (
        <VideoEmbed url={ex.video_url} title={ex.name} />
      ) : (
        ex.image_url && <img src={ex.image_url} alt={ex.name} className="w-full h-32 object-cover" />
      )}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <h4 className="font-heading font-semibold">{ex.name}</h4>
          {highlight && <span className="badge-success">Prescribed</span>}
        </div>
        <div className="flex gap-2 text-xs mt-1">
          <span className="badge-neutral">{ex.category}</span>
          <span className="badge-neutral">{ex.body_part}</span>
        </div>
        <p className="text-sm text-fg-secondary mt-2">{ex.description}</p>
        {ex.instructions && (
          <p className="text-xs text-fg-muted mt-1.5 leading-relaxed" data-testid={`ex-instructions-${ex.exercise_id}`}>
            <span className="font-medium text-fg-secondary">How:</span> {ex.instructions}
          </p>
        )}
        <div className="text-xs text-fg-muted mt-2 flex items-center justify-between">
          <div className="flex gap-3">
            <span>{ex.sets || 0}×{ex.reps || 0}</span>
            <span>{ex.duration_min || 0} min</span>
          </div>
          {highlight && onDone && (
            <button onClick={onDone} disabled={logging}
                    className="inline-flex items-center gap-1 text-brand text-xs font-medium hover:underline disabled:opacity-50"
                    data-testid={`done-${ex.exercise_id}`}>
              <Check className="w-3.5 h-3.5"/> {logging ? 'Logging…' : 'Mark done'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
